import { Habit, HabitColor, defaultColors } from "@/context/SupabaseHabitContext";
import { MoodType, MoodEntry } from "@/context/MoodTrackerContext";

export interface ImportedHabit {
  name: string;
  status: 'Active' | 'Paused';
  completedDates: string[];
}

export interface ImportedMoodData {
  entries: { date: string; mood: MoodType }[];
}

export interface ImportResult {
  success: boolean;
  message: string;
  importedHabits?: ImportedHabit[];
  importedMoodData?: ImportedMoodData;
  errors?: string[];
}

export function parseCSVContent(csvContent: string): ImportResult {
  try {
    const lines = csvContent.trim().split('\n');
    
    if (lines.length < 2) {
      return {
        success: false,
        message: "CSV file appears to be empty or invalid"
      };
    }

    // Parse header
    const header = lines[0];
    const headerCells = header.split(',').map(cell => cell.replace(/"/g, '').trim());
    
    // Check if this is the new format with Type column
    const hasTypeColumn = headerCells.includes("Type");
    
    if (hasTypeColumn) {
      // New format with Type, Name, Created At, Status, Completed Dates, Mood Data
      const expectedHeaders = ["Type", "Name", "Created At", "Status", "Completed Dates", "Mood Data"];
      const hasValidHeaders = expectedHeaders.every(expectedHeader => 
        headerCells.some(cell => cell === expectedHeader)
      );

      if (!hasValidHeaders) {
        return {
          success: false,
          message: "CSV format is invalid. Expected headers: " + expectedHeaders.join(", ")
        };
      }

      return parseNewFormat(lines);
    } else {
      // Legacy format - Habit Name, Created At, Status, Completed Dates
      const expectedHeaders = ["Habit Name", "Created At", "Status", "Completed Dates"];
      const hasValidHeaders = expectedHeaders.every(expectedHeader => 
        headerCells.some(cell => cell === expectedHeader)
      );

      if (!hasValidHeaders) {
        return {
          success: false,
          message: "CSV format is invalid. Expected headers: " + expectedHeaders.join(", ")
        };
      }

      return parseLegacyFormat(lines);
    }

  } catch (error) {
    return {
      success: false,
      message: `Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

function parseNewFormat(lines: string[]): ImportResult {
  const importedHabits: ImportedHabit[] = [];
  let importedMoodData: ImportedMoodData | undefined;
  const errors: string[] = [];

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    try {
      const cells = parseCSVRow(line);
      
      if (cells.length < 6) {
        errors.push(`Row ${i + 1}: Insufficient data`);
        continue;
      }

      const [type, name, createdAt, status, completedDatesStr, moodDataStr] = cells;

      if (type === "Habit") {
        // Parse habit data
        if (!name || name.trim() === '') {
          errors.push(`Row ${i + 1}: Habit name is required`);
          continue;
        }

        if (status !== 'Active' && status !== 'Paused') {
          errors.push(`Row ${i + 1}: Status must be 'Active' or 'Paused'`);
          continue;
        }

        let completedDates: string[] = [];
        if (completedDatesStr && completedDatesStr.trim() !== '') {
          completedDates = completedDatesStr
            .split(',')
            .map(date => date.trim())
            .filter(date => date !== '')
            .filter(date => {
              const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
              if (!dateRegex.test(date)) {
                errors.push(`Row ${i + 1}: Invalid date format '${date}'. Expected YYYY-MM-DD`);
                return false;
              }
              return true;
            });
        }

        importedHabits.push({
          name: name.trim(),
          status: status as 'Active' | 'Paused',
          completedDates
        });

      } else if (type === "Mood") {
        // Parse mood data
        if (moodDataStr && moodDataStr.trim() !== '') {
          const moodEntries: { date: string; mood: MoodType }[] = [];
          
          const moodPairs = moodDataStr.split(',').map(pair => pair.trim()).filter(pair => pair !== '');
          
          for (const pair of moodPairs) {
            const [date, mood] = pair.split(':').map(part => part.trim());
            
            if (!date || !mood) {
              errors.push(`Row ${i + 1}: Invalid mood data format '${pair}'. Expected 'YYYY-MM-DD:Mood'`);
              continue;
            }

            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
              errors.push(`Row ${i + 1}: Invalid date format '${date}'. Expected YYYY-MM-DD`);
              continue;
            }

            if (!['Easy', 'Meh', 'Struggled'].includes(mood)) {
              errors.push(`Row ${i + 1}: Invalid mood '${mood}'. Expected 'Easy', 'Meh', or 'Struggled'`);
              continue;
            }

            moodEntries.push({ date, mood: mood as MoodType });
          }

          if (moodEntries.length > 0) {
            // Merge with existing mood data if any
            if (importedMoodData) {
              importedMoodData.entries.push(...moodEntries);
            } else {
              importedMoodData = { entries: moodEntries };
            }
          }
        }
      }

    } catch (error) {
      errors.push(`Row ${i + 1}: Failed to parse - ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  if (importedHabits.length === 0 && !importedMoodData) {
    return {
      success: false,
      message: "No valid habits or mood data found in the CSV file",
      errors
    };
  }

  return {
    success: true,
    message: `Successfully parsed ${importedHabits.length} habit(s)${importedMoodData ? ` and ${importedMoodData.entries.length} mood entries` : ''}`,
    importedHabits,
    importedMoodData,
    errors: errors.length > 0 ? errors : undefined
  };
}

function parseLegacyFormat(lines: string[]): ImportResult {
  const importedHabits: ImportedHabit[] = [];
  const errors: string[] = [];

  // Parse data rows (legacy format - habits only)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    try {
      const cells = parseCSVRow(line);
      
      if (cells.length < 4) {
        errors.push(`Row ${i + 1}: Insufficient data`);
        continue;
      }

      const [name, createdAt, status, completedDatesStr] = cells;

      // Validate habit name
      if (!name || name.trim() === '') {
        errors.push(`Row ${i + 1}: Habit name is required`);
        continue;
      }

      // Validate status
      if (status !== 'Active' && status !== 'Paused') {
        errors.push(`Row ${i + 1}: Status must be 'Active' or 'Paused'`);
        continue;
      }

      // Parse completed dates
      let completedDates: string[] = [];
      if (completedDatesStr && completedDatesStr.trim() !== '') {
        completedDates = completedDatesStr
          .split(',')
          .map(date => date.trim())
          .filter(date => date !== '')
          .filter(date => {
            // Validate date format (YYYY-MM-DD)
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
              errors.push(`Row ${i + 1}: Invalid date format '${date}'. Expected YYYY-MM-DD`);
              return false;
            }
            return true;
          });
      }

      importedHabits.push({
        name: name.trim(),
        status: status as 'Active' | 'Paused',
        completedDates
      });

    } catch (error) {
      errors.push(`Row ${i + 1}: Failed to parse - ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  if (importedHabits.length === 0) {
    return {
      success: false,
      message: "No valid habits found in the CSV file",
      errors
    };
  }

  return {
    success: true,
    message: `Successfully parsed ${importedHabits.length} habit(s)`,
    importedHabits,
    errors: errors.length > 0 ? errors : undefined
  };
}

// Helper function to parse a CSV row with proper quote handling
function parseCSVRow(row: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < row.length) {
    const char = row[i];
    
    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // End of cell
      cells.push(current);
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }
  
  // Add the last cell
  cells.push(current);
  
  return cells;
}

export interface ImportHabitData {
  name: string;
  status: 'Active' | 'Paused';
  completedDates: string[];
  isNew: boolean;
  existingHabitId?: string;
  mergedDates?: string[];
}

export function prepareImportData(
  importedHabits: ImportedHabit[],
  existingHabits: Habit[]
): ImportHabitData[] {
  return importedHabits.map(importedHabit => {
    // Check if habit with same name already exists
    const existingHabit = existingHabits.find(h => 
      h.name.toLowerCase().trim() === importedHabit.name.toLowerCase().trim()
    );

    if (existingHabit) {
      // Merge with existing habit
      const existingDates = new Set(existingHabit.completedDates);
      const importedDates = new Set(importedHabit.completedDates);
      
      // Combine dates (union of both sets)
      const mergedDates = Array.from(new Set([...existingDates, ...importedDates])).sort();
      
      return {
        name: importedHabit.name,
        status: importedHabit.status,
        completedDates: importedHabit.completedDates,
        isNew: false,
        existingHabitId: existingHabit.id,
        mergedDates
      };
    } else {
      return {
        name: importedHabit.name,
        status: importedHabit.status,
        completedDates: importedHabit.completedDates,
        isNew: true
      };
    }
  });
}

// Function to create a habit with completion dates using direct database operations
export async function createHabitWithCompletions(
  name: string,
  color: HabitColor,
  completedDates: string[],
  status: 'Active' | 'Paused',
  userId: string
) {
  const { supabase } = await import("@/integrations/supabase/client");
  
  try {
    // Create the habit first
    const { data: habitData, error: habitError } = await supabase
      .from('habits')
      .insert({
        name,
        color_empty: color.empty,
        color_filled: color.filled,
        color_hover: color.hover,
        user_id: userId,
        active: status === 'Active',
        order: 0
      })
      .select()
      .single();
    
    if (habitError) throw habitError;
    
    // Add completion dates if any
    if (completedDates.length > 0) {
      const completionsToAdd = completedDates.map(date => ({
        habit_id: habitData.id,
        date
      }));
      
      const { error: completionsError } = await supabase
        .from('habit_completions')
        .insert(completionsToAdd);
      
      if (completionsError) throw completionsError;
    }
    
    return {
      success: true,
      habitId: habitData.id,
      message: `Created "${name}" with ${completedDates.length} completion dates`
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to create "${name}": ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
} 